// HScroll — a horizontally-scrollable strip with a HIDDEN scrollbar and a
// gradient edge-fade affordance: a soft fade at each edge that is scrollable,
// signalling "more →" / "← more", shown only while actually scrollable that way.
// The one home for the nav-strip / tab-bar / chip-row pattern that was ad-hoc
// (PrimaryNav had the good version with the right-edge fade; tabs + chips had a
// raw, ugly native scrollbar and no affordance).
//
// The fade is a gradient OVERLAY that blends to the surface behind the strip
// (`fadeColor`) — so the clipped item dissolves into the background, exactly like
// the main nav. Logical edges (start/end) flip under RTL; the gradient direction
// is computed from the resolved direction so the fade always points inward.
// Tracked with scroll + ResizeObserver (no breakpoints) — the fade self-hides
// when the strip fits or is scrolled to that edge.

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

export interface HScrollProps {
  children: ReactNode;
  /** Classes on the scroller itself (flex layout, gap, height). */
  className?: string;
  /** The surface background the edge fades blend to (the bg behind the strip). */
  fadeColor?: string;
  /** Edge-fade width. */
  fadeWidth?: string;
  /** Stretch the scroller to its parent's height (so flex children — e.g. tabs in
   *  a tall condensed bar — fill it and a bottom underline lands on the edge). */
  fill?: boolean;
}

export function HScroll({
  children,
  className = '',
  fadeColor = 'var(--color-bg-page)',
  fadeWidth = '1.75rem',
  fill = false,
}: HScrollProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [{ start, end, rtl }, setState] = useState({ start: false, end: false, rtl: false });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const isRtl = getComputedStyle(el).direction === 'rtl';
      const max = el.scrollWidth - el.clientWidth;
      // scrollLeft is 0 at the start edge and grows away from it in both
      // directions (negative under RTL in modern engines) — abs normalizes both.
      const x = Math.abs(el.scrollLeft);
      setState({ start: x > 1, end: x < max - 1, rtl: isRtl });
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, []);

  const fade = 'pointer-events-none absolute inset-y-0';
  const startStyle: CSSProperties = {
    width: fadeWidth,
    backgroundImage: `linear-gradient(${rtl ? 'to left' : 'to right'}, ${fadeColor}, transparent)`,
  };
  const endStyle: CSSProperties = {
    width: fadeWidth,
    backgroundImage: `linear-gradient(${rtl ? 'to right' : 'to left'}, ${fadeColor}, transparent)`,
  };

  return (
    <div className={`relative min-w-0 ${fill ? 'h-full' : ''}`}>
      <div ref={ref} className={`overflow-x-auto scrollbar-none ${fill ? 'h-full' : ''} ${className}`}>
        {children}
      </div>
      {/* Absolute siblings AFTER the scroller → paint over its clipped edges. */}
      {start && <span aria-hidden className={`${fade} start-0`} style={startStyle} />}
      {end && <span aria-hidden className={`${fade} end-0`} style={endStyle} />}
    </div>
  );
}
